<template>
<div>
    <div class="custom-search">
        <b-row>
            <b-col class="col-md-2 mb-1">
                <label for="accounting-number-input">{{ $t('accountingBalanceReport.starting_accounting_no') }}</label>
                <b-form-input size="sm" v-model="startingAccountingNo" :placeholder="$t('accountingBalanceReport.starting_accounting_no')" type="text" class="d-inline-block" v-on:keyup.enter="getAccountingBalanceReportList()" />
            </b-col>
            <b-col class="col-md-2 mb-1">
                <label for="accounting-number-input">{{ $t('accountingBalanceReport.ending_accounting_no') }}</label>
                <b-form-input size="sm" v-model="endingAccountingNo" :placeholder="$t('accountingBalanceReport.ending_accounting_no')" type="text" class="d-inline-block" v-on:keyup.enter="getAccountingBalanceReportList()" />
            </b-col>
            <b-col class="col-md-1 mb-1">
                <label for="currency-input">{{ $t('accountingBalanceReport.currency') }}</label>
                <b-form>
                    <b-form-select id="currency-input" v-model="currencySelect" size="sm" value-field="id" text-field="code" :options="currencyOption" @change="getAccountingBalanceReportList()" />
                </b-form>
            </b-col>
            <b-col class="col-md-1 mb-1">
                <label for="accounting-book-type-input">{{ $t('accountingBalanceReport.accounting_book_type') }}</label>
                <b-form>
                    <b-form-select id="accounting-book-type-input" v-model="accountingBookTypeSelect" size="sm" value-field="id" text-field="name" :options="accountingBookTypeOption" @change="getAccountingBalanceReportList()" />
                </b-form>
            </b-col>
            <b-col class="col-md-2 mb-1">
                <label for="start-date">{{ $t('accountingBalanceReport.start_date') }}</label>
                <b-form-group>
                    <b-form-datepicker today-button :label-today-button="$t('dateTime.today')" :placeholder="$t('accountingBalanceReport.start_date')" size="sm" v-model="startDate" class="mb1" defaultDate:null reset-button :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" :locale="$i18n.locale" :label-reset-button="$t('dateTime.reset')" :label-no-date-selected="$t('dateTime.select_date')" @input="getAccountingBalanceReportList()" />
                </b-form-group>
            </b-col>
            <b-col class="col-md-2 mb-1">
                <label for="end-date">{{ $t('accountingBalanceReport.end_date') }}</label>
                <b-form-group>
                    <b-form-datepicker today-button :label-today-button="$t('dateTime.today')" :placeholder="$t('accountingBalanceReport.end_date')" size="sm" v-model="endDate" class="mb1" defaultDate:null reset-button :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" :locale="$i18n.locale" :label-reset-button="$t('dateTime.reset')" :label-no-date-selected="$t('dateTime.select_date')" @input="getAccountingBalanceReportList()" />
                </b-form-group>
            </b-col>
            <b-col class="col-md-2 custom-search d-flex justify-content-end">
                <div class="d-flex align-items-center">
                    <b-button class="d-inline-block mr-05" size="sm" variant="outline-primary" @click="getAccountingBalanceReportList()">{{ $t('others.list') }}</b-button>
                    <b-button style="float:right;" class="ml-1" size="sm" variant="outline-primary" @click="exportExcel()">{{ $t('excel.excel') }}</b-button>
                </div>
            </b-col>
        </b-row>
    </div>
    <!-- table -->
    <vue-good-table ref="accountingBalanceReportList" :columns="columns" :rows="rows" styleClass="vgt-table condensed tableSize" :search-options="{
                          enabled: false,
                          externalQuery: searchTerm }" :select-options="{
                          enabled: false,
                          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
                          selectionInfoClass: 'custom-class',
                          selectionText: 'rows selected',
                          clearSelectionText: 'clear',
                          disableSelectInfo: true, // disable the select info panel on top
                          selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
                        }" :pagination-options="{
                          enabled: true,
                          perPage:pageLength
                        }" theme="my-theme">
        <div slot="emptystate">
            {{ $t('dataTable.data_not_found') }}
        </div>
        <!-- Row: Table Row -->
        <template slot="table-column" slot-scope="props">
            <span v-if="props.column.field ==='accountingNumber'" class="text-nowrap">
                {{ $t('accountingBalanceReport.accounting_no') }}
            </span>
            <span v-else-if="props.column.field === 'accountingTransactionType'" class="text-nowrap">
                {{ $t('accountingBalanceReport.transaction_type') }}
            </span>
            <span v-else-if="props.column.field ==='accountingTransactionDate'" class="text-nowrap">
                {{ $t('accountingBalanceReport.transaction_date') }}
            </span>
            <span v-else-if="props.column.field === 'currency'" class="text-nowrap">
                {{ $t('accountingBalanceReport.currency') }}
            </span>
            <span v-else-if="props.column.field === 'creditAmount'" class="text-nowrap">
                {{ $t('accountingBalanceReport.credit_amount') }}
            </span>
            <span v-else-if="props.column.field === 'debtAmount'" class="text-nowrap">
                {{ $t('accountingBalanceReport.debt_amount') }}
            </span>
            <span v-else-if="props.column.field === 'creditAmountCV'" class="text-nowrap">
                {{ $t('accountingBalanceReport.credit_amount_cv') +' '+ selectedCurrencyCode }}
            </span>
            <span v-else-if="props.column.field === 'debtAmountCV'" class="text-nowrap">
                {{ $t('accountingBalanceReport.debt_amount_cv') +' '+ selectedCurrencyCode }}
            </span>
            <span v-else-if="props.column.field ==='accountingTransactionDescription'" class="text-nowrap">
                {{ $t('accountingBalanceReport.transaction_description') }}
            </span>
            <span v-else-if="props.column.field ==='balance'" class="text-nowrap">
                {{ $t('accountingBalanceReport.balance') }}
            </span>
            <span v-else-if="props.column.field === 'process'" class="text-nowrap">
                {{ $t('others.process') }}
            </span>
        </template>
        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                    <span class="text-nowrap"> {{ $t('dataTable.total') }} {{ props.total }} {{ $t('dataTable.record') }}
                    </span>
                    <b-form-select v-model="pageLength" :options="['10', '50', '100', '1000']" class="mx-1" @input="(value) => props.perPageChanged({ currentPerPage: value })" />
                    <span class="text-nowrap"> {{ $t('dataTable.row_is_show') }} </span>
                </div>
                <div>
                    <b-pagination :value="1" :total-rows="props.total" :per-page="pageLength" first-number last-number align="right" prev-class="prev-item" next-class="next-item" class="mt-1 mb-0" @input="(value) => props.pageChanged({ currentPage: value })">
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </template>
    </vue-good-table>
</div>
</template>

<script>
export default {
    data() {
        return {
            total: 0,
            pageLength: 10,
            searchTerm: '',
            dir: false,
            pageNo: 1,
            name: '',
            startDate: null,
            endDate: null,
            rows: [],
            columns: [{
                    label: this.$t('accountingBalanceReport.accounting_no'),
                    field: `accountingNumber`,
                },
                {
                    label: this.$t('accountingBalanceReport.accounting_name'),
                    field: `name`,
                },
                {
                    label: this.$t('accountingBalanceReport.currency'),
                    field: `currency`,
                },
                {
                    label: this.$t('accountingBalanceReport.debt_amount_cv') + this.selectedCurrencyCode,
                    field: `debtAmountCV`,
                    formatFn: this.$formatAmount
                },
                {
                    label: this.$t('accountingBalanceReport.credit_amount_cv')  + this.selectedCurrencyCode,
                    field: `creditAmountCV`,
                    formatFn: this.$formatAmount
                },
                {
                    label: this.$t('accountingBalanceReport.debt_balance'),
                    field: `debtAmount`,
                    formatFn: this.$formatAmount
                },
                {
                    label: this.$t('accountingBalanceReport.credit_balance'),
                    field: `creditAmount`,
                    formatFn: this.$formatAmount
                }
            ],
            startingAccountingNo: '',
            endingAccountingNo: '',
            currencyOption: [],
            currencySelect: 0,
            startDate: null,
            endDate: null,
            accountingBookTypeOption: [],
            accountingBookTypeSelect: 0,
            selectedCurrencyCode : ''
        }
    },
    props: {
        subsidiaryLedgerId: {
            type: Number
        }
    },
    mounted() {
        this.currencySelect = 4
        this.getAccountingBalanceReportList()
    },
    watch: {
        currencySelect(newVal) {
            if (newVal && this.currencyOption && this.currencyOption.length > 0) {
                const selected = this.currencyOption.find(item => item.id === newVal);
                const selectedCode = selected ? selected.code : '';
                this.selectedCurrencyCode = selectedCode;
            }
        }
    },
    methods: {
        getAccountingBalanceReportList() {
            this.rows = []
            const controller = {
                name: 'Accounting',
                actionName: 'GetAccountingBalanceReport'
            }
            const data = {
                pageLength: this.pageLength,
                pageNo: this.pageNo,
                startDate: this.startDate,
                endDate: this.endDate,
                subsidiaryLedgerId: this.subsidiaryLedgerId,
                startingAccountingNo: this.startingAccountingNo,
                endingAccountingNo: this.endingAccountingNo,
                currencyId: this.currencySelect,
                accountingBookTypeId: this.accountingBookTypeSelect
            }
            const payload = {
                data: data,
                controller: controller
            }
            let loader = this.$loading.show()
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    this.currencyOption = response.data.currencyList || []
                    this.currencyOption.splice(0, 0, this.$nullSelected("code"))
                    this.accountingBookTypeOption = response.data.accountingBookTypeList || []
                    this.accountingBookTypeOption.splice(0, 0, this.$nullSelected())
                    this.total = response.data.total || 0
                    this.rows = Array.isArray(response.data.accountingBalanceReportList) ?
                        response.data.accountingBalanceReportList : []
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                    this.rows = []
                }
                loader.hide()
            }).catch((error) => {
                this.rows = []
                loader.hide()
            })
        },
        // async getInfo(id) {
        //     this.$refs.accountingBooksReceiptFormModal.showInfo(id)
        // },
        exportExcel() {
            const controller = {
                name: 'Accounting',
                actionName: 'CreateAccountingBalanceReportExcel'
            }
            const data = {
                startDate: this.startDate,
                endDate: this.endDate,
                subsidiaryLedgerId: this.subsidiaryLedgerId,
                startingAccountingNo: this.startingAccountingNo,
                endingAccountingNo: this.endingAccountingNo,
                currencyId: this.currencySelect,
                accountingBookTypeId: this.accountingBookTypeSelect

            }
            const payload = {
                data: data,
                controller: controller
            }
            let loader = this.$loading.show()
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    this.$downloadFile("Mizan Raporu", response.data.base64, 'xlsx')
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
                loader.hide()
            })
        }
    },
}
</script>

<style lang="scss">
.excel-css {
    float: right;
    margin-right: 1em;
    width: 9.5em;
    color: #7367f0;
    border: 1px solid #7367f0;
    background-color: transparent;
    margin-left: 1em;
    height: 2em;
    border-radius: 0.358rem;
}

.tableSize {
    font-size: 13px !important;
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    table-layout: auto;
}
</style>
